import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { describeAxiosError } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import RequireCompany from "@/components/RequireCompany";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Save, ArrowLeft, CloudDownload } from "lucide-react";
import { toast } from "sonner";

const DEFAULT_PROFILE = {
  taxpayer_type: "Individual",
  pan: "",
  name: "",
  date_of_birth: "",
  gender: "",
  residential_status: "Resident",
  tax_regime: "New",
  is_senior_citizen: false,
  is_super_senior_citizen: false,
  company_scheme: "",
  turnover_le_400cr: false,
  address: "",
  aadhaar_last4: "",
};

function Inner() {
  const { activeId } = useCompany();
  const navigate = useNavigate();
  const [reference, setReference] = useState(null);
  const [company, setCompany] = useState(null);
  const [profile, setProfile] = useState(DEFAULT_PROFILE);
  const [existing, setExisting] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!activeId) return;
    (async () => {
      try {
        const [r, p, c] = await Promise.all([
          api.get(`/tax/reference`),
          api.get(`/companies/${activeId}/tax/profile`),
          api.get(`/companies/${activeId}`),
        ]);
        setReference(r.data);
        setCompany(c.data);
        // Identity fields are OWNED by the company — always mirror them here.
        const identity = {
          name: c.data?.name || "",
          pan: (c.data?.pan || "").toUpperCase(),
          address: c.data?.address || "",
        };
        if (p.data) {
          setProfile({ ...DEFAULT_PROFILE, ...p.data, ...identity });
          setExisting(p.data);
        } else {
          setProfile((prev) => ({ ...prev, ...identity }));
        }
      } catch (err) {
        toast.error(describeAxiosError(err));
      }
    })();
  }, [activeId]);

  const onDobChange = (dob) => {
    let senior = false, superSenior = false;
    if (dob) {
      const age = Math.floor((Date.now() - new Date(dob).getTime()) / (365.25 * 24 * 60 * 60 * 1000));
      senior = age >= 60;
      superSenior = age >= 80;
    }
    setProfile((p) => ({ ...p, date_of_birth: dob, is_senior_citizen: senior, is_super_senior_citizen: superSenior }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...profile };
      // Normalize PAN uppercase
      payload.pan = (payload.pan || "").toUpperCase().trim();
      await api.post(`/companies/${activeId}/tax/profile`, payload);
      toast.success("Taxpayer profile saved");
      navigate("/tax");
    } catch (err) {
      toast.error(describeAxiosError(err));
    } finally {
      setSaving(false);
    }
  };

  if (!reference) return <div className="p-6 text-sm text-gray-500">Loading…</div>;

  const isIndividual = profile.taxpayer_type === "Individual";
  const isCompany = profile.taxpayer_type === "Company";
  const isHUF = profile.taxpayer_type === "HUF";

  return (
    <>
      <PageHeader
        title={existing ? "Edit Taxpayer Profile" : "Setup Taxpayer Profile"}
        subtitle="Applied to all tax computations for this company. Regime election is critical — pick carefully."
        actions={
          <>
            <Button variant="outline" onClick={() => navigate("/tax")} className="h-8" data-testid="btn-tax-profile-cancel">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <Button onClick={save} disabled={saving} className="h-8 bg-blue-700 hover:bg-blue-800" data-testid="btn-tax-profile-save">
              <Save className="w-4 h-4 mr-1" /> {saving ? "Saving…" : "Save Profile"}
            </Button>
          </>
        }
      />
      <div className="p-6 max-w-3xl">
        <div className="border border-gray-200 bg-white rounded p-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wider text-gray-600">Taxpayer Type *</Label>
              <select
                value={profile.taxpayer_type}
                onChange={(e) => setProfile({ ...profile, taxpayer_type: e.target.value })}
                className="h-9 w-full rounded border border-gray-300 px-2 text-sm bg-white"
                data-testid="select-taxpayer-type"
              >
                {reference.taxpayer_types.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>

            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wider text-gray-600">Residential Status *</Label>
              <select
                value={profile.residential_status}
                onChange={(e) => setProfile({ ...profile, residential_status: e.target.value })}
                className="h-9 w-full rounded border border-gray-300 px-2 text-sm bg-white"
                data-testid="select-residential-status"
              >
                {reference.residential_status.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            <div className="space-y-1 md:col-span-2">
              <Label className="text-xs uppercase tracking-wider text-gray-600 flex items-center gap-2">
                Name (as per PAN)
                <span className="text-[10px] normal-case tracking-normal px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 font-medium">From Company</span>
              </Label>
              <Input value={profile.name} readOnly disabled placeholder="Set in Company settings" className="bg-gray-50 text-gray-700 cursor-not-allowed" data-testid="input-pan-name" />
            </div>

            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wider text-gray-600 flex items-center gap-2">
                PAN
                <span className="text-[10px] normal-case tracking-normal px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 font-medium">From Company</span>
              </Label>
              <Input
                value={profile.pan}
                readOnly
                disabled
                placeholder="Set in Company settings"
                maxLength={10}
                className="font-mono uppercase bg-gray-50 text-gray-700 cursor-not-allowed"
                data-testid="input-pan"
              />
            </div>

            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wider text-gray-600">Aadhaar (last 4 digits)</Label>
              <Input value={profile.aadhaar_last4} onChange={(e) => setProfile({ ...profile, aadhaar_last4: e.target.value })} placeholder="1234" maxLength={4} className="font-mono" data-testid="input-aadhaar-last4" />
            </div>

            <div className="md:col-span-2 -mt-1">
              {!(company?.pan || "").trim() ? (
                <div className="flex items-start gap-2 p-2.5 border border-amber-300 bg-amber-50 rounded text-xs text-amber-900" data-testid="pan-missing-warning">
                  <span className="flex-1">
                    No PAN found for this company. PAN &amp; Name are captured from the
                    <strong> Company</strong> record and cannot be edited here.
                    Add the PAN in Company settings to file income tax.
                  </span>
                  <button
                    type="button"
                    onClick={() => navigate("/companies")}
                    className="whitespace-nowrap text-blue-700 hover:underline font-medium"
                    data-testid="btn-goto-company-settings"
                  >
                    Edit Company →
                  </button>
                </div>
              ) : (
                <p className="text-[11px] text-gray-500">
                  Name &amp; PAN are captured from the Company record and are locked here.
                  To change them, edit the company under{" "}
                  <button type="button" onClick={() => navigate("/companies")} className="text-blue-700 hover:underline" data-testid="link-company-settings">Companies</button>.
                </p>
              )}
            </div>
          </div>

          {/* Regime picker */}
          <div className="border-t border-gray-100 pt-4 space-y-3" data-testid="regime-section">
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-600 font-semibold">Tax Regime *</Label>
              <p className="text-xs text-gray-500 mt-0.5">
                Under the <strong>New Regime</strong>, most Chapter VI-A deductions and standard exemptions
                are not available (only 80CCD(2) & 80JJAA). Under the <strong>Old Regime</strong>, all
                deductions apply. This choice can be changed year-on-year (Individual / HUF).
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {reference.regimes.map((r) => (
                <label
                  key={r}
                  className={`flex items-start gap-2 border rounded p-3 cursor-pointer transition ${profile.tax_regime === r ? "border-blue-700 bg-blue-50/50 ring-1 ring-blue-700" : "border-gray-200 hover:border-gray-300"}`}
                  data-testid={`regime-option-${r.toLowerCase()}`}
                >
                  <input
                    type="radio"
                    checked={profile.tax_regime === r}
                    onChange={() => setProfile({ ...profile, tax_regime: r })}
                    className="mt-1"
                  />
                  <div>
                    <div className="font-medium text-sm">{r} Regime</div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {r === "New"
                        ? "Default from FY 2023-24. Lower slabs, ₹75k std ded. 87A rebate up to ₹12L income."
                        : "Retains all deductions (80C ₹1.5L, 80D, HRA, LTA, etc). ₹50k std ded. 87A rebate up to ₹5L."}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {isIndividual && (
            <div className="border-t border-gray-100 pt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wider text-gray-600">Date of Birth</Label>
                <Input type="date" value={profile.date_of_birth} onChange={(e) => onDobChange(e.target.value)} data-testid="input-dob" />
                {profile.is_super_senior_citizen && <p className="text-xs text-amber-700">Super Senior Citizen (age ≥ 80)</p>}
                {profile.is_senior_citizen && !profile.is_super_senior_citizen && <p className="text-xs text-blue-700">Senior Citizen (age ≥ 60)</p>}
              </div>
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wider text-gray-600">Gender</Label>
                <select
                  value={profile.gender}
                  onChange={(e) => setProfile({ ...profile, gender: e.target.value })}
                  className="h-9 w-full rounded border border-gray-300 px-2 text-sm bg-white"
                >
                  <option value="">—</option>
                  <option value="M">Male</option>
                  <option value="F">Female</option>
                  <option value="O">Other</option>
                </select>
              </div>
            </div>
          )}

          {isHUF && (
            <div className="border-t border-gray-100 pt-4 text-xs text-gray-600 bg-gray-50 rounded p-3">
              HUF is taxed at the same slabs as Individual (below 60). No senior-citizen benefit applies to HUF.
            </div>
          )}

          {isCompany && (
            <div className="border-t border-gray-100 pt-4 space-y-3">
              <Label className="text-xs uppercase tracking-wider text-gray-600 font-semibold">Company Options</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-gray-600">Special Scheme</Label>
                  <select
                    value={profile.company_scheme}
                    onChange={(e) => setProfile({ ...profile, company_scheme: e.target.value })}
                    className="h-9 w-full rounded border border-gray-300 px-2 text-sm bg-white"
                    data-testid="select-company-scheme"
                  >
                    <option value="">Standard (30% / 25%)</option>
                    <option value="115BAA">Section 115BAA (22% flat)</option>
                    <option value="115BAB">Section 115BAB (15% new mfg.)</option>
                  </select>
                </div>
                <label className="flex items-center gap-2 text-sm mt-6">
                  <input
                    type="checkbox"
                    checked={profile.turnover_le_400cr}
                    onChange={(e) => setProfile({ ...profile, turnover_le_400cr: e.target.checked })}
                    data-testid="check-turnover-400cr"
                  />
                  <span>Turnover ≤ ₹400 Cr (in specified year) → 25% rate</span>
                </label>
              </div>
            </div>
          )}

          <div className="border-t border-gray-100 pt-4 space-y-1">
            <Label className="text-xs uppercase tracking-wider text-gray-600 flex items-center gap-2">
              Address
              <span className="text-[10px] normal-case tracking-normal px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 font-medium">From Company</span>
            </Label>
            <Input value={profile.address} readOnly disabled placeholder="Set in Company settings" className="bg-gray-50 text-gray-700 cursor-not-allowed" data-testid="input-tax-address" />
          </div>

          {/* Income Tax Portal — consent-based fetch (no password stored) */}
          <div className="border-t border-gray-100 pt-4" data-testid="profile-portal-section">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border border-blue-200 bg-blue-50/50 rounded p-3.5">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                  <CloudDownload className="w-4 h-4 text-blue-700" /> Income Tax Portal
                </div>
                <p className="text-xs text-gray-600 mt-0.5">
                  Fetch AIS, TIS, Form 26AS, past returns, notices & tax paid directly — using your company PAN
                  {company?.pan ? <> (<span className="font-mono">{(company.pan || "").toUpperCase()}</span>)</> : ""} and a one-time OTP consent. No income-tax portal password is stored.
                </p>
              </div>
              <Button onClick={() => navigate("/tax/portal")} disabled={!(company?.pan || "").trim()} className="h-9 bg-blue-700 hover:bg-blue-800 shrink-0 disabled:opacity-50" data-testid="btn-profile-fetch-portal">
                <CloudDownload className="w-4 h-4 mr-1.5" /> Fetch details from income tax portal
              </Button>
            </div>
            {!(company?.pan || "").trim() && (
              <p className="text-[11px] text-amber-700 mt-1">Add the company PAN first to enable portal fetch.</p>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default function TaxpayerProfile() {
  return <RequireCompany><Inner /></RequireCompany>;
}
